import { allocateMinor, percentOf, roundMinor, sumMinor, } from './money.js';
/* ------------------------------------------------------------------ *
 * Option keys
 *
 * A tier's `optionKey` is either '*' (applies to every configuration) or a
 * '|'-joined, sorted list of `groupId:valueId` segments. A key matches a
 * selection when EVERY one of its segments is selected. When several keys
 * match, the one with the most segments wins — so a product can have a
 * default ladder plus a steeper one for, say, Premium Plus stock.
 * ------------------------------------------------------------------ */
export const WILDCARD_KEY = '*';
export function buildOptionKey(segments) {
    if (segments.length === 0)
        return WILDCARD_KEY;
    return segments
        .map((s) => `${s.groupId}:${s.valueId}`)
        .sort()
        .join('|');
}
function keyMatchesSelection(optionKey, selected) {
    if (optionKey === WILDCARD_KEY)
        return true;
    const chosen = new Set(selected.map((s) => `${s.groupId}:${s.valueId}`));
    return optionKey.split('|').every((seg) => chosen.has(seg));
}
function keySpecificity(optionKey) {
    return optionKey === WILDCARD_KEY ? 0 : optionKey.split('|').length;
}
/**
 * Narrows a product's full tier list to the single most specific ladder that
 * applies to the given selection.
 */
/**
 * The first option group chosen more than once, or null.
 *
 * Every option input is single-choice (select, swatch, radio), so a repeated
 * group never comes from the configurator - only from a forged request, and
 * it was exploitable: resolveTierSet kept one value's ladder while
 * resolvePrintSpec and shipping used the other, so A3 + A4 priced as A4 and
 * printed as A3 (22 Oct 2026 review).
 */
export function repeatedOptionGroup(selected) {
    const seen = new Set();
    for (const { groupId } of selected) {
        if (seen.has(groupId))
            return groupId;
        seen.add(groupId);
    }
    return null;
}
export function resolveTierSet(tiers, selected) {
    const applicable = tiers.filter((t) => keyMatchesSelection(t.optionKey, selected));
    if (applicable.length === 0)
        return [];
    let bestKey = WILDCARD_KEY;
    let bestScore = -1;
    for (const tier of applicable) {
        const score = keySpecificity(tier.optionKey);
        if (score > bestScore) {
            bestScore = score;
            bestKey = tier.optionKey;
        }
    }
    return applicable.filter((t) => t.optionKey === bestKey).sort((a, b) => a.qty - b.qty);
}
/**
 * The cheapest ladder a customer could actually reach, whatever they pick.
 *
 * `resolveTierSet(tiers, [])` answers "what does it cost before you choose
 * anything", and for a product whose every tier is keyed to a specific option
 * combination the honest answer is nothing at all — it returns an empty list.
 * That is right for the page, which waits for a selection, and wrong for
 * structured data, which has no selection to wait for: five products emitted an
 * AggregateOffer with no `lowPrice` and `offerCount: 0`, which Search Console
 * flagged as invalid and which cost them their price snippet entirely.
 *
 * So the snippet falls back to the least expensive option's ladder. It is a
 * real, orderable price rather than an average or a guess, and it matches what
 * "from ₹X" means everywhere else on the site.
 */
/**
 * The rungs of a ladder a customer can actually order.
 *
 * Raising a product's minimum quantity does not delete the rungs beneath it,
 * and every "from \u20B9X" on the site is derived from the ladder's lowest rung.
 * On 26 Sep 2026, after the catalogue-wide minimums landed,
 * tray-liners-placemats still carried a qty-1 rung and so advertised
 * "1 from \u20B914.70" against a real minimum of 100 from \u20B9777 \u2014 on the tile, in
 * the meta description, and in the Merchant Center Offer, which is the one
 * place a price that disagrees with the page costs the listing.
 *
 * The fallback is deliberate: a minimum set above the entire ladder should
 * leave the price alone rather than turn a priced product into "Price on
 * request", which is a worse answer than a slightly stale one.
 */
export function orderableTiers(tiers, minQty) {
    const floor = minQty ?? 1;
    const usable = tiers.filter((t) => t.qty >= floor);
    return usable.length > 0 ? usable : [...tiers];
}
export function bestAvailableTierSet(tiers) {
    const byKey = new Map();
    for (const tier of tiers) {
        const list = byKey.get(tier.optionKey) ?? [];
        list.push(tier);
        byKey.set(tier.optionKey, list);
    }
    let best = [];
    let bestEntryMinor = Number.POSITIVE_INFINITY;
    for (const list of byKey.values()) {
        const sorted = [...list].sort((a, b) => a.qty - b.qty);
        const entry = sorted[0];
        if (entry && entry.priceMinor < bestEntryMinor) {
            bestEntryMinor = entry.priceMinor;
            best = sorted;
        }
    }
    return best;
}
/**
 * Picks the tier for a quantity. An exact match wins. Otherwise the largest
 * tier at or below `qty` is used and its per-unit rate extrapolated — which is
 * only reachable when the product sets `allowCustomQty`.
 */
export function resolveTier(tierSet, qty) {
    if (tierSet.length === 0)
        return null;
    const exact = tierSet.find((t) => t.qty === qty);
    if (exact)
        return { tier: exact, extrapolated: false };
    let below = null;
    for (const tier of tierSet) {
        if (tier.qty <= qty && (below === null || tier.qty > below.qty))
            below = tier;
    }
    // Below the smallest tier: fall back to the smallest so we never under-quote.
    const chosen = below ?? tierSet[0];
    return { tier: chosen, extrapolated: true };
}
/** Per-unit rate implied by a tier, in fractional paise (do not round yet). */
function tierUnitRate(tier) {
    return tier.qty > 0 ? tier.priceMinor / tier.qty : 0;
}
/**
 * The line total for `qty` from a tier ladder, guaranteed **monotonic** in qty.
 *
 * An exact tier wins. Between two tiers the *total* is linearly interpolated, so
 * ordering more never costs less — the old behaviour extrapolated at the lower
 * tier's (higher) unit rate, which meant 99 posters could cost more than 100.
 * Above the top tier we extend at the top tier's unit rate; below the smallest
 * tier (only reachable via allowCustomQty under the smallest tier) we hold the
 * smallest tier's unit rate so a one-off is never under-quoted.
 */
export function tierTotalFor(tierSet, qty) {
    if (tierSet.length === 0)
        return null;
    const sorted = [...tierSet].sort((a, b) => a.qty - b.qty);
    const exact = sorted.find((t) => t.qty === qty);
    if (exact)
        return exact.priceMinor;
    const bottom = sorted[0];
    const top = sorted[sorted.length - 1];
    if (qty <= bottom.qty)
        return roundMinor(tierUnitRate(bottom) * qty);
    if (qty >= top.qty)
        return roundMinor(top.priceMinor + tierUnitRate(top) * (qty - top.qty));
    // Interpolate the total between the bracketing tiers.
    let lo = bottom;
    let hi = top;
    for (let i = 0; i < sorted.length - 1; i++) {
        if (sorted[i].qty <= qty && qty <= sorted[i + 1].qty) {
            lo = sorted[i];
            hi = sorted[i + 1];
            break;
        }
    }
    const frac = (qty - lo.qty) / (hi.qty - lo.qty);
    return roundMinor(lo.priceMinor + (hi.priceMinor - lo.priceMinor) * frac);
}
/** Sum of a size grid, floored to whole positive units. The line quantity. */
export function sumSizeQuantities(sizeQuantities) {
    if (!sizeQuantities)
        return 0;
    return Object.values(sizeQuantities).reduce((s, n) => s + (Number.isFinite(n) && n > 0 ? Math.floor(n) : 0), 0);
}
/**
 * How much a size-variable line costs relative to the product's base size — the
 * ratio of the chosen area to the base area, clamped so a tiny size never goes
 * near-free and a huge one stays bounded. Returns 1 for products without
 * `customSize` (and when no size is chosen), so it is safe to multiply every
 * line by it. The same function runs in the browser and in the server quote, so
 * the price shown and the price charged cannot drift.
 */
export function sizeAreaMultiplier(product, wMm, hMm) {
    const cs = product.customSize;
    if (!cs || !wMm || !hMm)
        return 1;
    const w = Math.min(Math.max(wMm, cs.minWmm), cs.maxWmm);
    const h = Math.min(Math.max(hMm, cs.minHmm), cs.maxHmm);
    const ratio = (w * h) / (cs.baseWmm * cs.baseHmm);
    // The customSize min/max already bound the real input, so the clamp is only a
    // guard against bad data. It must be generous enough for large-format sq-ft
    // pricing (a base of 1 sq ft against a 100 sq ft banner is a 100x multiplier),
    // which the old 20x cap silently under-charged.
    return Math.min(1000, Math.max(0.25, ratio));
}
/** One square foot in mm². The unit large-format print is quoted in. */
const SQ_FT_MM2 = 304.8 * 304.8;
/**
 * What one square foot of a per-area product costs.
 *
 * Banners, flex, canvas and foam boards carry no tier ladder at all: the
 * migration that moved them to sq-ft pricing set `basePriceMinor` to the rate
 * and the base size to a 1 ft square, so for today's data this simply returns
 * `basePriceMinor`. It is derived from the base area anyway rather than assumed,
 * so a product given a base that is not exactly one square foot still quotes a
 * true per-sq-ft rate instead of a silently wrong one.
 */
export function perSqFtRateMinor(product) {
    const cs = product.customSize;
    if (!cs)
        return null;
    const baseMm2 = cs.baseWmm * cs.baseHmm;
    if (baseMm2 <= 0)
        return null;
    return roundMinor((product.basePriceMinor * SQ_FT_MM2) / baseMm2);
}
/**
 * The cheapest thing a customer could actually order on a per-area product:
 * the smallest size its own bounds allow, at the minimum quantity.
 *
 * This is the sq-ft counterpart to `bestAvailableTierSet`. Those products have
 * no ladder for it to fall back to, so their structured data had no price at
 * all — and since the shipping and returns markup hangs off the offer, they lost
 * that too. Mirrors how the Configurator prices a line (subtotal rounded, then
 * scaled by area and rounded again) so the snippet cannot drift from the panel.
 */
export function areaEntryPriceMinor(product) {
    const cs = product.customSize;
    if (!cs)
        return null;
    const subtotal = roundMinor(product.basePriceMinor * product.minQty);
    const scaled = roundMinor(subtotal * sizeAreaMultiplier(product, cs.minWmm, cs.minHmm));
    // The floor is part of the entry price, not a surcharge on top of it. Without
    // this the Offer advertised Rs25 for a banner nobody could buy for less than
    // Rs150 — the same page-versus-feed mismatch that cost the ladder products
    // their Merchant Center listings, arriving by the other road.
    return Math.max(scaled, (product.minLineChargeMinor ?? 0) * product.minQty);
}
/**
 * Volume discount for a per-area product, which has no tier ladder to carry one.
 *
 * Banners and boards price at a flat rate per square foot, so twenty of them
 * cost exactly twenty times one. Every laddered product in the catalogue gets
 * cheaper per unit as quantity rises; these were the only things that did not.
 *
 * The break points come from what the two competitors actually charge, measured
 * 27 Sep 2026 by putting quantities into their own configurators:
 *
 *   Printo, Large Format Banner 2x3ft non-tearable — one step only:
 *     1-4  Rs1,148.14/pc (100%)      5+  Rs1,053.74/pc (91.78%), flat to 500
 *   Vistaprint India, Fabric Banners — a published ladder:
 *     1 (100%)  2-4 (96%)  5-10 (92%)  25 (90.4%)  50+ (88%)
 *
 * Ours undercuts whichever of the two is better at every break, by at least a
 * point. Stated as a multiplier of the per-unit rate rather than as a "% off"
 * because that is how it is applied, and a discount expressed two ways is a
 * discount that eventually disagrees with itself.
 */
export const AREA_QTY_DISCOUNTS = [
    { minQty: 50, multiplier: 0.87 },
    { minQty: 25, multiplier: 0.89 },
    { minQty: 5, multiplier: 0.905 },
    { minQty: 2, multiplier: 0.95 },
];
/** The multiplier for `qty`, or 1 below the first break. Descending, so the first match wins. */
export function areaQtyMultiplier(qty) {
    for (const step of AREA_QTY_DISCOUNTS) {
        if (qty >= step.minQty)
            return step.multiplier;
    }
    return 1;
}
/**
 * Scale a priced line by its chosen area, then hold it at the product's floor.
 *
 * ONE FUNCTION BECAUSE THERE ARE TWO CALLERS. The area multiplier used to be
 * applied separately in `quote.ts` and in `Configurator.tsx`, which is two
 * places to remember a third step — and they already disagreed slightly, since
 * each rounded the subtotal and the unit price independently, so unit x qty did
 * not always equal the subtotal. Here the unit price is the one rounded figure
 * and the subtotal is derived from it, so the two cannot drift from each other
 * or from the server.
 *
 * The floor is per UNIT, not per line: the handling it pays for is per banner.
 *
 * A product without `customSize` is returned untouched — the multiplier is 1 for
 * it anyway, and a floor on a laddered product would fight its own rungs.
 */
export function areaScaledLine(product, line, qty, wMm, hMm) {
    if (!product.customSize)
        return { subtotalMinor: line.subtotalMinor, unitPriceMinor: line.unitPriceMinor };
    const mult = sizeAreaMultiplier(product, wMm, hMm);
    const floor = product.minLineChargeMinor ?? 0;
    const unitPriceMinor = Math.max(roundMinor(line.unitPriceMinor * mult), floor);
    return { unitPriceMinor, subtotalMinor: roundMinor(unitPriceMinor * Math.max(1, qty)) };
}
export class PricingError extends Error {
    code;
    constructor(message, code) {
        super(message);
        this.code = code;
        this.name = 'PricingError';
    }
}
function findValue(groups, groupId, valueId) {
    const group = groups.find((g) => g.id === groupId);
    return group?.values?.find((v) => v.id === valueId) ?? null;
}
/**
 * Validates a configuration: every required group answered, every selection real.
 * Throws PricingError. Called by the storefront for UX and again server-side at
 * checkout — the server call is the authoritative one.
 */
export function validateSelection(optionGroups, selected) {
    for (const group of optionGroups) {
        // A quantity-axis group (size grid) is satisfied by the size quantities, not
        // by a single selection — priceLine validates it separately.
        if (!group.required || group.quantityAxis)
            continue;
        if (!selected.some((s) => s.groupId === group.id)) {
            throw new PricingError(`Please choose a ${group.name}.`, 'MISSING_OPTION');
        }
    }
    for (const sel of selected) {
        if (!findValue(optionGroups, sel.groupId, sel.valueId)) {
            throw new PricingError(`"${sel.valueName}" is no longer available for ${sel.groupName}.`, 'UNKNOWN_OPTION');
        }
    }
}
export function validateQty(product, tierSet, qty) {
    if (!Number.isInteger(qty) || qty <= 0) {
        throw new PricingError('Quantity must be a whole number.', 'QTY_TOO_LOW');
    }
    if (qty < product.minQty) {
        throw new PricingError(`Minimum order is ${product.minQty}.`, 'QTY_TOO_LOW');
    }
    if (product.maxQty != null && qty > product.maxQty) {
        throw new PricingError(`Maximum order is ${product.maxQty}.`, 'QTY_TOO_HIGH');
    }
    // Ganging step: the press lays these up N-to-a-sheet, so a quantity that is
    // not a whole multiple of the step cannot actually be produced.
    if (product.qtyStep != null && product.qtyStep > 1 && qty % product.qtyStep !== 0) {
        throw new PricingError(`Quantity must be a multiple of ${product.qtyStep}.`, 'QTY_NOT_A_TIER');
    }
    if (!product.allowCustomQty && tierSet.length > 0 && !tierSet.some((t) => t.qty === qty)) {
        throw new PricingError('Please pick one of the listed quantities.', 'QTY_NOT_A_TIER');
    }
}
/**
 * The single source of truth for what a line item costs, exclusive of tax.
 *
 * Application order is fixed and must not be reordered — changing it changes
 * every quoted price:
 *   1. base        = tier total for the quantity (or basePrice x qty)
 *   2. + perUnit   = qty x modifier, for each perUnit option
 *   3. + percent   = percentage OF THE BASE ONLY, for each percent option
 *   4. + flat      = added once per line, for each flat option
 *
 * Percentages deliberately apply to the base rather than the running total, so
 * two +10% options give +20%, not +21%.
 */
export function priceLine(input) {
    const { product, optionGroups, selected, tiers, qty, sizeBreakdown, placements, decorations, } = input;
    validateSelection(optionGroups, selected);
    // Quantity-axis (size grid): the line quantity is the sum of the per-size
    // quantities. Require at least one unit and that the caller's qty matches, so
    // minQty is then enforced on the TOTAL — never per size.
    const axis = optionGroups.find((g) => g.quantityAxis);
    if (axis) {
        const sizeTotal = (sizeBreakdown ?? []).reduce((s, x) => s + Math.max(0, Math.floor(x.qty)), 0);
        if (sizeTotal <= 0) {
            throw new PricingError(`Please enter a quantity for at least one ${axis.name}.`, 'MISSING_OPTION');
        }
        if (qty !== sizeTotal) {
            throw new PricingError('Quantity must equal the sum of the sizes.', 'QTY_NOT_A_TIER');
        }
    }
    const tierSet = resolveTierSet(tiers, selected);
    validateQty(product, tierSet, qty);
    const breakdown = [];
    const tierTotal = tierTotalFor(tierSet, qty);
    /*
     * The volume discount applies ONLY on the no-ladder branch, and only to a
     * product priced by area. A laddered product already carries its discount in
     * the rungs, so applying this too would discount it twice — fabric-banners is
     * exactly that case, since it has both `customSize` and a ladder. Gating on
     * `tierTotal == null` rather than on the product alone is what keeps them
     * apart, and it is why this lives inside priceLine rather than beside
     * sizeAreaMultiplier at the call sites: there are two of those and they would
     * drift.
     */
    const baseMinor = tierTotal != null
        ? tierTotal
        : roundMinor(product.basePriceMinor * qty * (product.customSize ? areaQtyMultiplier(qty) : 1));
    breakdown.push({
        label: `${qty} x base`,
        amountMinor: baseMinor,
        kind: 'base',
    });
    let perUnitMinor = 0;
    let percentMinor = 0;
    let flatMinor = 0;
    for (const sel of selected) {
        const value = findValue(optionGroups, sel.groupId, sel.valueId);
        if (!value || value.modifierValue === 0)
            continue;
        let amount;
        switch (value.modifierType) {
            case 'perUnit':
                amount = roundMinor(value.modifierValue * qty);
                perUnitMinor += amount;
                break;
            case 'percent':
                amount = percentOf(baseMinor, value.modifierValue);
                percentMinor += amount;
                break;
            case 'flat':
            default:
                amount = roundMinor(value.modifierValue);
                flatMinor += amount;
                break;
        }
        breakdown.push({
            label: `${sel.groupName}: ${sel.valueName}`,
            amountMinor: amount,
            kind: 'option',
        });
    }
    // Per-size upcharges (size grid): each size's modifier is charged per unit of
    // that size — flat = value × sizeQty; percent = % of that size's base share.
    let sizeMinor = 0;
    if (sizeBreakdown) {
        for (const s of sizeBreakdown) {
            const n = Math.max(0, Math.floor(s.qty));
            if (n === 0 || !s.modifierValue)
                continue;
            const amount = s.modifierType === 'percent'
                ? percentOf(roundMinor((baseMinor * n) / qty), s.modifierValue)
                : roundMinor(s.modifierValue * n);
            sizeMinor += amount;
            breakdown.push({ label: `${s.valueName} ×${n}`, amountMinor: amount, kind: 'option' });
        }
    }
    /*
     * Decoration surcharges, per unit per decorated position.
     *
     * After the options and before the subtotal, so a decorated placement shows
     * as its own breakdown line rather than disappearing into the base — a
     * customer who adds a back print should be able to see what it cost them.
     * An unknown placementId contributes nothing instead of throwing: the line
     * is already priced and refusing to quote it over a stale id would block a
     * checkout for a cosmetic mismatch.
     */
    let decorationMinor = 0;
    for (const decoration of decorations ?? []) {
        const placement = (placements ?? []).find((pl) => pl.id === decoration.placementId);
        if (!placement?.surchargeMinor)
            continue;
        const amount = roundMinor(placement.surchargeMinor * qty);
        decorationMinor += amount;
        breakdown.push({
            label: `${placement.label} ×${qty}`,
            amountMinor: amount,
            kind: 'option',
        });
    }
    const subtotalMinor = baseMinor + perUnitMinor + percentMinor + flatMinor + sizeMinor + decorationMinor;
    return {
        unitPriceMinor: qty > 0 ? roundMinor(subtotalMinor / qty) : 0,
        subtotalMinor,
        breakdown,
    };
}
/**
 * Cheapest per-unit rate across a tier ladder — powers the "Save 30%" badges
 * and the "from ₹X" figures on category tiles.
 */
export function bestUnitRateMinor(tierSet) {
    if (tierSet.length === 0)
        return null;
    return roundMinor(Math.min(...tierSet.map(tierUnitRate)));
}
/**
 * What a listing card says about price, derived from the full tier list.
 *
 * Stored on the product as `priceSummary` by the `onPriceTierWritten` trigger
 * so a card needs no tier reads: a category page used to read every rung of
 * every product it showed — `/pricing` was ~15,000 reads a render, most of
 * October 2026's Firestore bill. The storefront falls back to computing this
 * live only for a product the trigger has not reached yet.
 */
export function cardPriceSummary(allTiers, minQty) {
    // Only rungs a customer can actually order — raising a minimum leaves the
    // rungs beneath it in place, and every figure below is derived from the
    // ladder's shape.
    const tiers = orderableTiers([...allTiers].sort((a, b) => a.qty - b.qty), minQty);
    // resolveTierSet with nothing selected only finds the unkeyed ('*') ladder.
    // 58 live products price per size and have NO unkeyed ladder at all — every
    // tier is keyed 'banner-size:v_0' and so on — so the card read as "Price on
    // request" while the PDP, where a size IS selected, showed real money. The
    // card must answer "from how much?", which across variants means the
    // cheapest variant.
    let ladder = resolveTierSet(tiers, []);
    if (ladder.length === 0 && tiers.length > 0) {
        const byKey = new Map();
        for (const t of tiers) {
            const list = byKey.get(t.optionKey);
            if (list)
                list.push(t);
            else
                byKey.set(t.optionKey, [t]);
        }
        let cheapest = null;
        for (const group of byKey.values()) {
            const sorted = [...group].sort((a, b) => a.qty - b.qty);
            const first = sorted[0];
            const bestFirst = cheapest ? [...cheapest].sort((a, b) => a.qty - b.qty)[0] : undefined;
            // Compare unit rate, not total: variants can have different entry
            // quantities, and the larger pack is not automatically dearer.
            if (!bestFirst || (first && first.priceMinor / first.qty < bestFirst.priceMinor / bestFirst.qty)) {
                cheapest = sorted;
            }
        }
        if (cheapest)
            ladder = cheapest;
    }
    const entry = ladder[0] ?? null;
    // The rung that actually yields the cheapest per-unit rate. Compared on unit
    // rate rather than assuming the largest quantity wins, because a ladder is
    // not required to be monotonic — Inkmonk's polo, which this catalogue
    // mirrors, is dearer per unit at 50 than at 30.
    const best = ladder.length
        ? ladder.reduce((a, b) => (a.priceMinor / a.qty <= b.priceMinor / b.qty ? a : b))
        : null;
    return {
        fromUnitMinor: bestUnitRateMinor(ladder),
        bestQty: best?.qty ?? null,
        entryQty: entry?.qty ?? null,
        entryTotalMinor: entry?.priceMinor ?? null,
    };
}
export function savingsPercentVsSmallestTier(tierSet, tier) {
    if (tierSet.length === 0)
        return 0;
    const smallest = tierSet.reduce((a, b) => (a.qty <= b.qty ? a : b));
    const baseRate = tierUnitRate(smallest);
    if (baseRate <= 0)
        return 0;
    const pct = ((baseRate - tierUnitRate(tier)) / baseRate) * 100;
    return Math.max(0, Math.round(pct));
}
/* ------------------------------------------------------------------ *
 * Order totals & GST
 * ------------------------------------------------------------------ */
/** Our place of supply. Intra-state -> CGST+SGST, inter-state -> IGST. */
export const HOME_STATE_CODE = '29'; // Karnataka
/**
 * Catalogue prices are quoted MRP-style, inclusive of GST — the Indian retail
 * norm and what vistaprint.in does ("Price below is MRP (inclusive of all
 * taxes)"). The consequence is that GST is *extracted* from the line total
 * rather than added to it, so the number on the product page is the number the
 * customer pays and the cart needs no tax row at all.
 *
 * Flip this to false only if the site moves to B2B tax-exclusive quoting.
 */
export const PRICES_INCLUDE_TAX = true;
/**
 * Splits a tax-inclusive amount into the net taxable value and the tax.
 * taxable = gross x 100 / (100 + rate), and the tax takes the rounding
 * remainder so the two always add back to exactly `gross`.
 */
export function extractTax(gross, ratePercent) {
    if (ratePercent <= 0)
        return { taxable: gross, tax: 0 };
    const taxable = roundMinor((gross * 100) / (100 + ratePercent));
    return { taxable, tax: gross - taxable };
}
/**
 * Discount is apportioned across lines by value before tax, so each line's GST
 * is charged on what the customer actually paid for it — required for a
 * compliant invoice when lines carry different GST rates.
 */
export function computeOrderTotals(input) {
    const { lines, discountMinor = 0, shippingMinor = 0, placeOfSupplyStateCode, homeStateCode = HOME_STATE_CODE, pricesIncludeTax = false, } = input;
    const itemsSubtotalMinor = sumMinor(lines.map((l) => l.subtotalMinor));
    const cappedDiscount = Math.min(discountMinor, itemsSubtotalMinor);
    const perLineDiscount = allocateMinor(cappedDiscount, lines.map((l) => l.subtotalMinor));
    const isIntraState = placeOfSupplyStateCode === homeStateCode;
    let cgstMinor = 0;
    let sgstMinor = 0;
    let igstMinor = 0;
    let taxableMinor = 0;
    const applyTax = (tax) => {
        if (isIntraState) {
            // Half each, with the odd paise going to CGST so the two sum to `tax`.
            const half = Math.floor(tax / 2);
            cgstMinor += tax - half;
            sgstMinor += half;
        }
        else {
            igstMinor += tax;
        }
    };
    lines.forEach((line, i) => {
        const lineGross = line.subtotalMinor - (perLineDiscount[i] ?? 0);
        if (pricesIncludeTax) {
            const { taxable, tax } = extractTax(lineGross, line.gstRatePercent);
            taxableMinor += taxable;
            applyTax(tax);
        }
        else {
            taxableMinor += lineGross;
            applyTax(percentOf(lineGross, line.gstRatePercent));
        }
    });
    // Shipping is taxed at 18% under SAC 9968 (goods transport). It is charged
    // by weight and destination - see shipping.ts.
    if (shippingMinor > 0) {
        if (pricesIncludeTax) {
            const { taxable, tax } = extractTax(shippingMinor, 18);
            taxableMinor += taxable;
            applyTax(tax);
        }
        else {
            taxableMinor += shippingMinor;
            applyTax(percentOf(shippingMinor, 18));
        }
    }
    /*
     * Settled to the rupee, and this is the number that is CHARGED.
     *
     * It used to be left in paise here while invoice.ts rounded its own copy, so
     * the tax invoice stated a total up to 50 paise away from the amount actually
     * collected — and since MRP-inclusive GST extraction leaves paise on most
     * orders, that was most orders. Payments and invoices could not be reconciled
     * against each other at all (bug #10 in docs/bug-audit-2026-07.md).
     *
     * Rounding here rather than un-rounding the invoice, because Indian tax
     * invoices are conventionally settled to the rupee and a customer paying
     * ₹1,119.79 for something the invoice calls ₹1,120 is the wrong way round.
     * The difference is carried as `roundOffMinor` so it is stated, not absorbed.
     */
    const beforeRoundingMinor = taxableMinor + cgstMinor + sgstMinor + igstMinor;
    const grandTotalMinor = Math.round(beforeRoundingMinor / 100) * 100;
    return {
        itemsSubtotalMinor,
        discountMinor: cappedDiscount,
        shippingMinor,
        taxableMinor,
        cgstMinor,
        sgstMinor,
        igstMinor,
        roundOffMinor: grandTotalMinor - beforeRoundingMinor,
        grandTotalMinor,
    };
}
//# sourceMappingURL=pricing.js.map